import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingScreen from './LoadingScreen';
import Website from '../website/Website';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const [isPWA, setIsPWA] = useState(false);
  const [pwaCheckComplete, setPwaCheckComplete] = useState(false);

  // PWA detection effect
  useEffect(() => {
    const detectPWA = () => {
      // Method 1: Check for display-mode: standalone
      const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
      
      // Method 2: Check for iOS standalone mode
      const isIOSStandalone = (window.navigator as any).standalone === true;
      
      // Method 3: Check if launched from home screen (Android)
      const isAndroidStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                                  document.referrer.includes('android-app://');
      
      // Method 4: Check for PWA-specific user agent patterns
      const userAgent = navigator.userAgent.toLowerCase();
      const isPWAUserAgent = userAgent.includes('wv') || // WebView
                             userAgent.includes('pwa') ||
                             document.referrer.startsWith('android-app://');
      
      const detectedPWA = isDisplayModeStandalone || isIOSStandalone || isAndroidStandalone || isPWAUserAgent;
      
      console.log('🔍 PWA Detection Results:', {
        isDisplayModeStandalone,
        isIOSStandalone,
        isAndroidStandalone,
        isPWAUserAgent,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        finalResult: detectedPWA
      });
      
      setIsPWA(detectedPWA);
      setPwaCheckComplete(true);
    };

    // Run detection immediately
    detectPWA();

    // Also listen for changes in display mode
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => {
      console.log('🔍 Display mode changed, re-detecting PWA');
      detectPWA();
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Show loading spinner while checking auth or PWA status
  if (loading || !pwaCheckComplete) {
    return <LoadingScreen />;
  }

  // If user is authenticated, show the app
  if (user) {
    console.log('🔐 AuthGuard: User authenticated, showing app');
    return <>{children}</>;
  }

  // User is not authenticated
  console.log('🔐 AuthGuard: User not authenticated', { isPWA });

  if (isPWA) {
    // PWA: Always redirect to login when not authenticated
    console.log('🔐 AuthGuard: PWA detected, redirecting to login');
    return <Navigate to="/login" replace />;
  } else {
    // Web: Show marketing website when not authenticated
    console.log('🔐 AuthGuard: Web browser detected, showing marketing website');
    return <Website />;
  }
}
